import { Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAsync } from 'react-use';

import { Button, LayoutGrid } from '@ifs-gcp/components';
import type { ITemplateNode } from '@ifs-gcp/core';
import { isQuestionNode } from '@ifs-gcp/core/helpers';
import type { CompanyGpsCoordinatesChild, QuestionChild } from '@ifs-gcp/core/models';

import type { FactoryElementProps } from '@/features/audit/audit-elements/factory-element';
import { getAssetFile } from '@/hooks/useJsonTemplate';
import { sizeToNumber } from '@/utils/card';
import { getTranslation } from '@/utils/translations';

import { GpsModal } from './gps-modal';
import styles from './widget-company-gps-coordinates.module.scss';

type WidgetCompanyGpsCoordinatesProps = {
  element: ITemplateNode<CompanyGpsCoordinatesChild>;
  FactoryElement: React.FC<FactoryElementProps>;
};

let initialLanguage: string | undefined;

export const WidgetCompanyGpsCoordinates = ({
  element,
  FactoryElement,
}: WidgetCompanyGpsCoordinatesProps) => {
  const {
    children,
    props: { triggerButtonText },
  } = element;

  const {
    t,
    i18n: { language },
  } = useTranslation();

  useEffect(() => {
    if (!initialLanguage) {
      initialLanguage = language;
    }
  }, [language]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tosConfirmed, setTosConfirmed] = useState(false);

  const countryList = useAsync(() => getAssetFile('options/countryList.json'));

  const canUseMaps = import.meta.env.VITE_API_KEY_MAPS && countryList.value && initialLanguage;

  const companyGpsLatitudeField = children.find(
    (item) => isQuestionNode(item) && item.props.id === 'companyGpsLatitude',
  );
  const companyGpsLongitudeField = children.find(
    (item) => isQuestionNode(item) && item.props.id === 'companyGpsLongitude',
  );

  return (
    <>
      <div className={styles.container}>
        <LayoutGrid container columnSpacing={2.5}>
          {children.map((item) => (
            <LayoutGrid key={item.props.id} item xs={sizeToNumber(item.props.size)}>
              <FactoryElement element={item} />
            </LayoutGrid>
          ))}
        </LayoutGrid>

        <div className={styles.buttonContainer}>
          <Button
            type="button"
            variant="contained"
            color="secondary"
            onClick={() => setIsModalOpen(true)}
            disabled={!canUseMaps}
          >
            {getTranslation(triggerButtonText)}
          </Button>
        </div>
      </div>

      {isModalOpen && canUseMaps && (
        <Suspense fallback={<div>{t('loading')}</div>}>
          <GpsModal
            element={element}
            countryList={countryList.value || []}
            language={initialLanguage || 'en'}
            companyGpsLatitudeField={companyGpsLatitudeField as QuestionChild}
            companyGpsLongitudeField={companyGpsLongitudeField as QuestionChild}
            tosConfirmed={tosConfirmed}
            setIsModalOpen={setIsModalOpen}
            setTosConfirmed={setTosConfirmed}
          />
        </Suspense>
      )}
    </>
  );
};
